import React from 'react';
import clsx from 'clsx';
import loaderSvg from '../../../../../../assets/aica-loader.svg';
import { isEmpty } from '../../../../../../utils/utils';
import { AuditedRowSpec, AUDITED_UNIT_DIVISOR, AUDITED_UNIT_DEFAULT } from '../../auditedStatementLayout';
import { useAuditedStatementTableLogic } from './useAuditedStatementTableLogic';
import styles from './AuditedStatementTable.module.scss';

type TableHeader = { name: string; key: string };
type TableRow = { classificationName?: string; [key: string]: any };

interface AuditedStatementTableProps {
    /** Figma-ordered row model for this statement (Balance Sheet / P&L / Cash Flow). */
    layout: AuditedRowSpec[];
    tableData: { headers?: TableHeader[]; rows?: TableRow[] };
    loading: boolean;
    /** Display unit for all figures ('Lakh' | 'Crore') — rescales each value at render time. Defaults to Lakh. */
    unit?: string;
    /** Shown instead of the table when the backend has no data at all for this statement. */
    emptyMessage: string;
    /** RECUR-27656 — reports the pixel widths of the real thead cells ([classification, ...values]) after layout/resize, for the sticky header clone. */
    onHeaderCellWidthsChange?: (widths: number[]) => void;
    /** RECUR-27656 — reports .TableContainer's scrollLeft on every horizontal scroll, so the sticky header clone can track it. */
    onHorizontalScroll?: (scrollLeft: number) => void;
    /** RECUR-27656 (Defect 2) — hides the real thead (visibility, not display, so it stays measurable) while a scroll-synced clone is pinned above it. */
    hideHeader?: boolean;
    /** RECUR-27786 — opt-in row-click for the Classification cell of 'item' rows (leaf
     * classifications only; 'subtotal'/'group'/'sub' rows never carry a raw record to drill
     * into). Audited never passes this — its rows stay non-clickable, unchanged. Provisional
     * passes it to preserve its existing click-to-drill-down/reclassify affordance. */
    onClassificationClick?: (backendRow: TableRow) => void;
}

const isBlank = (value: unknown) => value === undefined || value === null || value === '';

// Rescale a backend-formatted ₹ figure (e.g. "12,22,330", "-5,70,00,000") into the selected unit.
// INR (divisor 1, the default) shows the raw value untouched; Lakhs (÷1e5) / Crore (÷1e7) rescale
// and reformat with Indian grouping + 2 decimals. Non-numeric cells (blank, "—", "NA") pass through.
const rescaleValue = (value: unknown, unit: string): unknown => {
    const divisor = AUDITED_UNIT_DIVISOR[unit] ?? 1;
    if (divisor === 1) return value;
    if (typeof value !== 'string') return value;
    const trimmed = value.trim();
    if (trimmed === '' || trimmed === '—') return value;
    const num = Number(trimmed.replace(/,/g, ''));
    if (!Number.isFinite(num)) return value;
    return (num / divisor).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const renderCellValue = (value: unknown) => {
    if (isBlank(value)) return '—';
    return value as React.ReactNode;
};

/**
 * Presentational table for ONE audited-financials statement, built to the RECUR-27010
 * redesign mock. Renders the fixed Figma row model (`layout`), joining backend values by
 * `classificationName`. Rows are non-clickable — no chevrons, no drill-down / edit-
 * classification affordance on this surface; every row renders fully expanded. This REPLACES
 * ClassifiedTable for the audited redesign surface only; ClassifiedTable itself is untouched
 * and still backs Provisional Financials. Supports a pinned sticky-header clone (RECUR-27656):
 * `onHeaderCellWidthsChange` / `onHorizontalScroll` report the real thead's measured widths and
 * scroll position, and `hideHeader` hides the real thead while the clone is pinned above it.
 */
const AuditedStatementTable = ({
    layout,
    tableData,
    loading,
    unit = AUDITED_UNIT_DEFAULT,
    emptyMessage,
    onHeaderCellWidthsChange,
    onHorizontalScroll,
    hideHeader,
    onClassificationClick,
}: AuditedStatementTableProps) => {
    const headers = tableData?.headers ?? [];
    const rows = (tableData?.rows ?? []).filter((row) => !isEmpty(row));
    const valueHeaders = headers.filter((header) => header.name !== 'Classification');
    const hasData = !isEmpty(headers) && valueHeaders.length > 0 && rows.length > 0;

    const rowsBySrc = rows.reduce<Record<string, TableRow>>((acc, row) => {
        if (row.classificationName) acc[row.classificationName] = row;
        return acc;
    }, {});

    const { containerRef, theadRowRef, handleScroll } = useAuditedStatementTableLogic({
        hasData,
        tableVisible: !loading,
        valueHeadersLength: valueHeaders.length,
        unit,
        onHeaderCellWidthsChange,
        onHorizontalScroll,
    });

    // Group and sub rows are plain structural label rows — no affordance, no drill-down — and
    // differ only in the row's own class, so both branches below delegate here.
    const renderStructuralRow = (row: AuditedRowSpec, rowKey: string, rowClassName: string) => (
        <tr key={rowKey} className={rowClassName}>
            <td>{row.label}</td>
            {valueHeaders.map((header) => (
                <td key={header.key} />
            ))}
        </tr>
    );

    if (loading) {
        return (
            <div className={styles.LoadingContainer}>
                <img className={styles.LoaderImg} src={loaderSvg} alt="Loading..." height="30%" width="30%" />
            </div>
        );
    }

    if (!hasData) {
        return <div className={styles.Empty}>{emptyMessage}</div>;
    }

    return (
        <div className={styles.TableContainer} ref={containerRef} onScroll={(e) => handleScroll(e.currentTarget.scrollLeft)}>
            <table className={styles.Table}>
                <thead className={hideHeader ? styles.HeaderHidden : undefined}>
                    <tr ref={theadRowRef}>
                        <th className={styles.ClassificationHeaderCell}>Classification</th>
                        {valueHeaders.map((header) => (
                            <th key={header.key} className={styles.HeaderCell}>
                                {header.name}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {layout.map((row) => {
                        const rowKey = `${row.type}-${row.label}`;

                        if (row.type === 'group') {
                            // Render a real first cell + empty value cells (NOT a single colSpan
                            // cell) so the label uses the same sticky-first-column treatment as
                            // every other row: the light-blue heading stays pinned left while the
                            // (empty) value area scrolls horizontally under the band.
                            return renderStructuralRow(row, rowKey, styles.GroupRow);
                        }

                        if (row.type === 'sub') {
                            return renderStructuralRow(row, rowKey, styles.SubRow);
                        }

                        const backendRow = row.src ? rowsBySrc[row.src] : undefined;
                        const noSource = !row.src;
                        const isClickable = row.type === 'item' && !!onClassificationClick && !!backendRow && !backendRow.isAggregated;

                        return (
                            <tr
                                key={rowKey}
                                className={clsx({
                                    [styles.SubtotalRow]: row.type === 'subtotal',
                                    [styles.GrandTotalRow]: row.type === 'subtotal' && row.grand,
                                    [styles.ItemRow]: row.type === 'item',
                                })}
                            >
                                <td
                                    style={isClickable ? { cursor: 'pointer' } : undefined}
                                    onClick={isClickable ? () => onClassificationClick(backendRow as TableRow) : undefined}
                                >
                                    {row.label}
                                </td>
                                {valueHeaders.map((header) => {
                                    const value = rescaleValue(backendRow ? backendRow[header.key] : undefined, unit);
                                    const isNegative = typeof value === 'string' && value.trim().startsWith('-');
                                    return (
                                        <td
                                            key={header.key}
                                            className={clsx({
                                                [styles.Negative]: isNegative,
                                                [styles.NoSource]: noSource,
                                            })}
                                        >
                                            {renderCellValue(value)}
                                        </td>
                                    );
                                })}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

interface AuditedStatementHeaderCloneProps {
    valueHeaders: TableHeader[];
    /** Measured [classification, ...values] widths from the real thead — see `measure()` in useAuditedStatementTableLogic. */
    widths: number[];
}

/**
 * RECUR-27656 — pinned-band clone of the real thead row, shown while a statement is stuck to the
 * top of the scroller. Reuses this module's `.Table` classes (thead th styles, sticky first-cell
 * treatment) so it renders pixel-identical to the real header, with each cell's width pinned to
 * what was actually measured (auto table layout can stretch columns on a wide screen). Returns
 * null during the transient frame where widths haven't caught up yet with a Consolidated/
 * Standalone switch (header count changed but widths haven't been re-measured).
 */
export const AuditedStatementHeaderClone = ({ valueHeaders, widths }: AuditedStatementHeaderCloneProps) => {
    if (widths.length !== valueHeaders.length + 1) return null;

    const cellStyle = (width: number) => ({ width, minWidth: width, maxWidth: width });

    return (
        <table className={clsx(styles.Table, styles.CloneTable)}>
            <thead>
                <tr>
                    <th className={styles.ClassificationHeaderCell} style={cellStyle(widths[0])}>
                        Classification
                    </th>
                    {valueHeaders.map((header, index) => (
                        <th key={header.key} className={styles.HeaderCell} style={cellStyle(widths[index + 1])}>
                            {header.name}
                        </th>
                    ))}
                </tr>
            </thead>
        </table>
    );
};

export default AuditedStatementTable;
